import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import './Auth.css';
import { useLocation } from 'react-router-dom';
import { trackEvent } from "../../utils/analytics";



function DonorSignup() {
  const navigate = useNavigate();
  const location = useLocation();
const params = new URLSearchParams(location.search);
const pendingNeedId = params.get('need');
const pendingItemName = params.get('item');
const pendingOrgName = params.get('org');
const comingFromBrowse = params.get('browse') === 'true';

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  async function onSubmit(data) {
    try {
      setError('');
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      const user = userCredential.user;

      await setDoc(doc(db, 'donors', user.uid), {
        uid: user.uid,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        city: data.city,
        state: data.state,
        anonymityPreference: data.anonymityPreference,
        autoRedirect: data.autoRedirect === 'true',
        role: 'donor',
        activeSubscriptions: 0,
        totalImpact: 0,
        createdAt: serverTimestamp(),
        status: 'active'
      });

trackEvent("signup_completed", {
  method: "email",
  has_pending_need: !!pendingNeedId,
  pending_need_id: pendingNeedId || null
});

if (pendingNeedId) {
  navigate(`/give/adopt/${pendingNeedId}`);
} else {
  navigate('/give/browse');
}


    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  function getErrorMessage(code) {
    switch(code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">

        <div className="auth-logo" onClick={() => navigate('/')}>
          somebody.gives
        </div>

        {pendingItemName ? (
  <>
    <h1>You are one step away.</h1>
    <div className="signup-need-context">
      <div className="signup-need-label">You're taking responsibility for</div>
      <div className="signup-need-name">{pendingItemName}</div>
      <div className="signup-need-org">{pendingOrgName}</div>
    </div>
    <p className="auth-sub">
      Create your account to confirm your commitment.
      Takes less than two minutes.
    </p>
  </>
) : comingFromBrowse ? (
  <>
    <h1>Almost there.</h1>
    <p className="auth-sub">
      Create your account to take responsibility for a need.
      Takes less than two minutes.
    </p>
  </>
) : (
  <>
    <h1>Create your account</h1>
    <p className="auth-sub">
      Join somebody.gives and start making a difference
      in Springfield.
    </p>
  </>
)}


        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">

          <div className="form-row">
            <div className="form-group">
              <label>First name</label>
              <input
                {...register('firstName', { required: 'First name is required' })}
                placeholder="First name"
              />
              {errors.firstName &&
                <span className="field-error">{errors.firstName.message}</span>}
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input
                {...register('lastName', { required: 'Last name is required' })}
                placeholder="Last name"
              />
              {errors.lastName &&
                <span className="field-error">{errors.lastName.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Email address</label>
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Please enter a valid email'
                }
              })}
              placeholder="your@email.com"
              type="email"
            />
            {errors.email &&
              <span className="field-error">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              })}
              placeholder="At least 6 characters"
              type="password"
            />
            {errors.password &&
              <span className="field-error">{errors.password.message}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                {...register('city', { required: 'City is required' })}
                placeholder="Springfield"
                defaultValue="Springfield"
              />
              {errors.city &&
                <span className="field-error">{errors.city.message}</span>}
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                {...register('state', { required: 'State is required' })}
                placeholder="MO"
                defaultValue="MO"
                maxLength={2}
              />
              {errors.state &&
                <span className="field-error">{errors.state.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>How should your name appear on gift cards?</label>
            <select {...register('anonymityPreference')}>
              <option value="firstLastInitialCity">
                John S., Springfield MO (recommended)
              </option>
              <option value="anonymous">
                A generous donor from Springfield (anonymous)
              </option>
              <option value="fullName">
                Full name shown
              </option>
            </select>
          </div>

          <div className="form-group">
            <label>If a need you sponsor becomes unavailable:</label>
            <select {...register('autoRedirect')}>
              <option value="true">
                Automatically redirect to the next most critical need
                at the same org (recommended)
              </option>
              <option value="false">
                Notify me and let me choose
              </option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...register('terms', { required: 'You must agree to continue' })}
              />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank">terms of service</a>
              </span>
            </label>
            {errors.terms &&
              <span className="field-error">{errors.terms.message}</span>}
          </div>

          <button
            type="submit"
            className="btn-primary btn-full"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create account →'}
          </button>

        </form>

        <div className="auth-footer">
          Already have an account?{' '}
          <span onClick={() => navigate('/give/login')}>Sign in</span>
        </div>

      </div>
    </div>
  );
}

export default DonorSignup;